import { useEffect, useMemo, useReducer, useState } from 'react';
import { Check, Eye, EyeOff, Pencil, Save, Trash2, UserPlus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import {
  useGrantPermission,
  useProjectPermissions,
  useRevokePermission,
  useWorkspaceMembers,
  type Membership,
} from '@/lib/api';
import {
  buildRows,
  dirtyUserSubjects,
  emptyRow,
  gridReducer,
  isRowDirty,
  isRowEmpty,
  memberDisplay,
  toLanguageAccess,
  type CellValue,
  type GridState,
} from './permissionGridState';

interface Props {
  workspaceID: string;
  projectID: string;
  languages: string[];
  /** Override of the read-only signal (used by tests / parent guards). */
  readOnly?: boolean;
}

const CELL_LABEL: Record<CellValue, string> = {
  none: '—',
  read: 'R',
  write: 'W',
};

const CELL_TONE: Record<CellValue, 'muted' | 'primary' | 'positive'> = {
  none: 'muted',
  read: 'primary',
  write: 'positive',
};

const CYCLE: Record<CellValue, CellValue> = {
  none: 'read',
  read: 'write',
  write: 'none',
};

export function PermissionGrid({ workspaceID, projectID, languages, readOnly }: Props) {
  const permissions = useProjectPermissions(projectID);
  const members = useWorkspaceMembers(workspaceID);
  const grant = useGrantPermission();
  const revoke = useRevokePermission();
  const toast = useToast();

  const initialState: GridState = useMemo(() => ({ rows: {}, baseline: {} }), []);
  const [state, dispatch] = useReducer(gridReducer, initialState);
  const [adding, setAdding] = useState(false);

  // Sync server state into the grid whenever the underlying lists change.
  useEffect(() => {
    if (!permissions.data) return;
    const rows = buildRows(permissions.data, languages);
    dispatch({ type: 'sync', rows, baseline: rows });
  }, [permissions.data, languages]);

  const dirty = dirtyUserSubjects(state);

  async function saveRow(userSubject: string) {
    if (readOnly) return;
    try {
      const row = state.rows[userSubject];
      if (!row || isRowEmpty(row)) {
        await revoke.mutateAsync({ projectID, userSubject });
        toast.show('Revoked all access', 'success');
        dispatch({ type: 'remove-user', userSubject });
      } else {
        await grant.mutateAsync({
          projectID,
          userSubject,
          languages: toLanguageAccess(row),
        });
        toast.show('Permissions saved', 'success');
        dispatch({
          type: 'sync',
          rows: { ...state.rows },
          baseline: { ...state.rows, [userSubject]: { ...row } },
        });
      }
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function saveAll() {
    for (const userSubject of dirty) {
      await saveRow(userSubject);
    }
  }

  function resetRow(userSubject: string) {
    dispatch({ type: 'reset-row', userSubject });
  }

  function cycleCell(userSubject: string, langCode: string) {
    if (readOnly) return;
    const current = state.rows[userSubject]?.[langCode] ?? 'none';
    dispatch({ type: 'set-cell', userSubject, langCode, value: CYCLE[current] });
  }

  function setRow(userSubject: string, value: CellValue) {
    if (readOnly) return;
    dispatch({ type: 'set-row', userSubject, value });
  }

  function addUser(userSubject: string) {
    dispatch({ type: 'add-user', userSubject });
    setAdding(false);
  }

  if (permissions.isLoading || members.isLoading) {
    return <Spinner label="Loading permissions…" />;
  }

  if (permissions.isError) {
    return (
      <p
        role="alert"
        className="rounded-md border border-negative/40 bg-negative/10 p-3 text-sm text-negative"
      >
        Failed to load permissions: {permissions.error.message}
      </p>
    );
  }

  if (languages.length === 0) {
    return (
      <EmptyState
        title="No target languages yet"
        description="Add target languages in Settings to grant per-language permissions."
      />
    );
  }

  const subjects = Object.keys(state.rows);
  const availableToAdd = (members.data ?? []).filter((m) => !subjects.includes(m.userSubject));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Click a cell to cycle <Badge tone="muted">—</Badge>{' '}
          <Badge tone="primary">R</Badge> <Badge tone="positive">W</Badge>. Read-only users
          cannot mutate translations; Write also implies Read.
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setAdding(true)}
            disabled={readOnly || availableToAdd.length === 0}
          >
            <UserPlus aria-hidden />
            Grant access
          </Button>
          <Button
            onClick={() => void saveAll()}
            disabled={readOnly || dirty.length === 0 || grant.isPending || revoke.isPending}
          >
            <Save aria-hidden />
            Save all ({dirty.length})
          </Button>
        </div>
      </div>

      {subjects.length === 0 ? (
        <EmptyState
          title="No project-scoped permissions yet"
          description="By default, workspace owners and admins have full access. Grant per-language access to translators here."
          action={
            <Button onClick={() => setAdding(true)} disabled={availableToAdd.length === 0}>
              Grant access
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="sticky left-0 z-10 bg-muted/50 px-3 py-2 text-left font-medium">
                  Member
                </th>
                <th className="px-2 py-2 text-center font-medium">Row</th>
                {languages.map((code) => (
                  <th key={code} className="px-2 py-2 text-center font-medium">
                    {code}
                  </th>
                ))}
                <th className="w-24 px-2 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subjects.map((subject) => {
                const row = state.rows[subject] ?? emptyRow(languages);
                const meta = memberDisplay(subject, members.data ?? []);
                const rowDirty = isRowDirty(state, subject);
                return (
                  <tr key={subject} className={cn('border-t border-border', rowDirty && 'bg-warning/5')}>
                    <td className="sticky left-0 bg-card px-3 py-2 align-top">
                      <div className="font-medium text-foreground">{meta.primary}</div>
                      {meta.secondary && (
                        <div className="text-[11px] text-muted-foreground">{meta.secondary}</div>
                      )}
                      <code className="mt-1 inline-block rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground">
                        {subject}
                      </code>
                    </td>
                    <td className="px-2 py-2 align-top text-center">
                      <div className="inline-flex flex-col gap-1">
                        <button
                          type="button"
                          aria-label="Set entire row to Read"
                          onClick={() => setRow(subject, 'read')}
                          disabled={readOnly}
                          className="inline-flex h-6 w-12 items-center justify-center rounded-md border border-input bg-surface text-[11px] hover:bg-muted"
                        >
                          <Eye className="mr-1 size-3" aria-hidden /> R
                        </button>
                        <button
                          type="button"
                          aria-label="Set entire row to Write"
                          onClick={() => setRow(subject, 'write')}
                          disabled={readOnly}
                          className="inline-flex h-6 w-12 items-center justify-center rounded-md border border-input bg-surface text-[11px] hover:bg-muted"
                        >
                          <Pencil className="mr-1 size-3" aria-hidden /> W
                        </button>
                        <button
                          type="button"
                          aria-label="Clear entire row"
                          onClick={() => setRow(subject, 'none')}
                          disabled={readOnly}
                          className="inline-flex h-6 w-12 items-center justify-center rounded-md border border-input bg-surface text-[11px] hover:bg-muted"
                        >
                          <EyeOff className="mr-1 size-3" aria-hidden /> —
                        </button>
                      </div>
                    </td>
                    {languages.map((code) => {
                      const val = row[code] ?? 'none';
                      return (
                        <td key={code} className="px-2 py-2 text-center align-middle">
                          <button
                            type="button"
                            aria-label={`${meta.primary} access for ${code} — currently ${val}`}
                            onClick={() => cycleCell(subject, code)}
                            disabled={readOnly}
                            className={cn(
                              'inline-flex h-9 w-12 items-center justify-center rounded-md border text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                              val === 'none' && 'border-input bg-muted text-muted-foreground hover:bg-muted/80',
                              val === 'read' && 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/15',
                              val === 'write' && 'border-positive/40 bg-positive/15 text-positive hover:bg-positive/20',
                            )}
                          >
                            {CELL_LABEL[val]}
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-2 py-2 text-right align-top">
                      <div className="inline-flex flex-col items-end gap-1">
                        {rowDirty && (
                          <Button
                            size="sm"
                            onClick={() => void saveRow(subject)}
                            disabled={grant.isPending || revoke.isPending || readOnly}
                          >
                            <Check aria-hidden />
                            Save
                          </Button>
                        )}
                        {rowDirty && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => resetRow(subject)}
                            disabled={grant.isPending || revoke.isPending}
                          >
                            Reset
                          </Button>
                        )}
                        {!rowDirty && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setRow(subject, 'none');
                            }}
                            aria-label={`Revoke ${meta.primary}`}
                            disabled={readOnly}
                          >
                            <Trash2 aria-hidden className="text-negative" />
                            Revoke
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <GrantAccessDialog
        open={adding}
        onClose={() => setAdding(false)}
        members={availableToAdd}
        onSubmit={(subj) => addUser(subj)}
      />

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <span className="font-medium text-foreground">CellValue legend:</span>
        <Badge tone={CELL_TONE.none}>—</Badge>
        <span>no access</span>
        <Badge tone={CELL_TONE.read}>R</Badge>
        <span>read</span>
        <Badge tone={CELL_TONE.write}>W</Badge>
        <span>read + write</span>
      </div>
    </div>
  );
}

function GrantAccessDialog({
  open,
  onClose,
  members,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  members: Membership[];
  onSubmit: (userSubject: string) => void;
}) {
  const [subject, setSubject] = useState('');
  const [manual, setManual] = useState('');

  useEffect(() => {
    if (!open) {
      setSubject('');
      setManual('');
    } else {
      setSubject(members[0]?.userSubject ?? '');
    }
  }, [open, members]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Grant project access"
      description="Pick an existing workspace member, or paste a Keycloak subject for an external grant."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              const chosen = manual.trim() || subject;
              if (chosen) onSubmit(chosen);
            }}
            disabled={!manual.trim() && !subject}
          >
            Add row
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground" htmlFor="perm-subject">
            Workspace member
          </label>
          <select
            id="perm-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={members.length === 0}
            className="flex h-9 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {members.length === 0 ? (
              <option value="">No more members to add</option>
            ) : (
              members.map((m) => (
                <option key={m.id} value={m.userSubject}>
                  {m.displayName || m.email || m.userSubject} — {m.role}
                </option>
              ))
            )}
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground" htmlFor="perm-manual">
            …or paste a subject manually
          </label>
          <input
            id="perm-manual"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="e.g. an external translator's Keycloak sub"
            className="flex h-9 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>
    </Dialog>
  );
}
